import {
  ChannelManager,
  CugTier,
  GHAAccountType,
  GHAPropertyBrand,
  GHAPropertyType,
  SearchGHAPropertiesParams,
} from '~/services/ConnectionGoogleService';

import { ACCOUNT_TYPES_BY_BRAND, ACCOUNT_TYPE_LABELS, ACCOUNT_TYPE_ORDER, CUG_TIER_OPTIONS } from './constants';

/** Type guard for narrowing a raw URL/query string to a CugTier value. */
export function isCugTier(value: unknown): value is CugTier {
  return typeof value === 'string' && (CUG_TIER_OPTIONS as Array<string>).includes(value);
}

export function getAccountTypesForBrand(brand: GHAPropertyBrand): Array<GHAAccountType> {
  const allowed = ACCOUNT_TYPES_BY_BRAND[brand] ?? [];
  return ACCOUNT_TYPE_ORDER.filter((type) => allowed.includes(type));
}

export function getAccountTypeOptionsForBrand(
  brand: GHAPropertyBrand,
): Array<{ value: GHAAccountType; label: string }> {
  return getAccountTypesForBrand(brand).map((value) => ({
    value,
    label: ACCOUNT_TYPE_LABELS[value],
  }));
}

export function filterAccountTypesForBrand(
  accountTypes: Array<GHAAccountType> | undefined,
  brand: GHAPropertyBrand,
): Array<GHAAccountType> {
  if (!accountTypes || accountTypes.length === 0) {
    return [];
  }
  const allowed = getAccountTypesForBrand(brand);
  return accountTypes.filter((type) => allowed.includes(type));
}

export function isAccountTypeValidForBrand(accountType: GHAAccountType | undefined, brand: GHAPropertyBrand): boolean {
  if (!accountType) {
    return false;
  }
  return getAccountTypesForBrand(brand).includes(accountType);
}

function splitCommaSeparated(value: string): Array<string> {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

interface BuildFilterParamsInput {
  propertyIds: string;
  liveOnGoogle?: boolean;
  accountType?: GHAAccountType;
  blacklisted?: boolean;
  discountConfigured?: boolean;
  channelManagers: Array<ChannelManager>;
  ghaPropertyType?: GHAPropertyType;
  name: string;
  countryCodes: string;
  brand: GHAPropertyBrand;
  distanceGroups: Array<string>;
  chain?: string;
  cugTiers?: Array<CugTier>;
}

export function buildFilterParams(input: BuildFilterParamsInput): Omit<SearchGHAPropertiesParams, 'page' | 'limit'> {
  return {
    propertyIds: splitCommaSeparated(input.propertyIds),
    liveOnGoogle: input.liveOnGoogle,
    accountType: input.accountType,
    blacklisted: input.blacklisted,
    discountConfigured: input.discountConfigured,
    channelManagers: input.channelManagers,
    ghaPropertyTypes: input.ghaPropertyType ? [input.ghaPropertyType] : undefined,
    name: input.name || undefined,
    countryCodes: splitCommaSeparated(input.countryCodes),
    brand: input.brand,
    distanceGroups: input.distanceGroups.length > 0 ? input.distanceGroups : undefined,
    chain: input.chain && input.chain.trim().length > 0 ? input.chain.trim() : undefined,
    cugTiers: input.cugTiers && input.cugTiers.length > 0 ? input.cugTiers : undefined,
  };
}
